import type { GameState } from '@sd/sim';
import type { TradeGoodId } from '@sd/content';
import type { Viewer } from '../map/describe';
import { diplomacyView } from './diplomacyModel';
import { fleetView } from './fleetModel';
import { marketView, type LeverRow } from './marketModel';
import { planetView } from './planetModel';
import { researchView } from './researchModel';

/**
 * What the player could do right now, per tab and sub-tab (#152, the author's
 * request of 28.10.2026): a green count on a tab says there is something to
 * build, learn, lay down or switch on there. Pure: state in, counts out.
 */
export interface ActionCounts {
  /** Buildings that can be laid down now. */
  readonly planet: number;
  /** Technologies that can be started now. */
  readonly research: number;
  readonly market: { readonly levers: number; readonly contracts: number };
  /** Ship classes that can be laid down; ships with an order they can carry out here. */
  readonly fleet: { readonly yard: number; readonly ships: number };
  /** Proposals waiting for an answer. */
  readonly diplomacy: number;
}

export type CountedTab = 'planet' | 'market' | 'research' | 'fleet' | 'diplomacy';

/** A lever the player could pull now: ready, aimed at someone, or a dump of the picked good. */
export const leverReady = (l: LeverRow): boolean =>
  !l.locked &&
  !l.active &&
  (l.command !== null || l.targets.length > 0 || (l.id === 'dumping' && !l.reason));

export function actionCounts(
  state: GameState,
  v: Viewer,
  history: Partial<Record<TradeGoodId, readonly number[]>> = {},
): ActionCounts {
  const planet = planetView(state, v);
  const research = researchView(state, v);
  const market = marketView(state, v, history);
  const fleet = fleetView(state, v);
  const diplomacy = diplomacyView(state, v);
  return {
    // A building that fits both layers is one thing to do, not two.
    planet: new Set(planet?.options.filter((o) => o.offer.command).map((o) => o.id)).size,
    research: research?.nodes.filter((n) => n.status === 'open' && n.command).length ?? 0,
    market: {
      levers: market?.levers.filter(leverReady).length ?? 0,
      contracts: market?.contracts.filter((k) => k.actions.some((a) => a.primary)).length ?? 0,
    },
    fleet: {
      yard: fleet?.options.filter((o) => o.command).length ?? 0,
      ships: fleet?.fleets.filter((f) => f.ready).length ?? 0,
    },
    diplomacy:
      diplomacy?.rivals.reduce((n, r) => n + r.incoming.filter((a) => a.command).length, 0) ?? 0,
  };
}

/** A tab's count: the sum of its sub-tabs. */
export function tabCount(c: ActionCounts, tab: CountedTab): number {
  switch (tab) {
    case 'planet':
      return c.planet;
    case 'research':
      return c.research;
    case 'market':
      return c.market.levers + c.market.contracts;
    case 'fleet':
      return c.fleet.yard + c.fleet.ships;
    case 'diplomacy':
      return c.diplomacy;
  }
}
